import React, { useMemo, useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import { t } from '@lingui/macro';
import {
    Box, Typography, ToggleButtonGroup, ToggleButton,
    FormControlLabel, Switch, Stack, Chip
} from '@mui/material';
import NoResults from '@/assets/svg/no-results.svg?react';
import Chart, { ChartData, TooltipItem, Point } from '@/utils/chartjs-setup';
import { getColorForType, hexToRgba } from '../../lib/colorHelpers'
import { getTypeDisplayName } from '../../lib/typeFormatters'

export type MedianDurationTrendsProps = {
    data?: {
        medianByDay?: {
            day: string;
            median: number;
            p25: number;
            p75: number;
            type?: string;
        }[];
        dataQuality?: {
            totalConsultations: number;
            validDurations: number;
            qualityScore: number;
        };
    };
    title: string;
    includeByType?: boolean;
    selectedType?: string | null;
};

export function MedianDurationTrendsComponent({
    data,
    title,
    includeByType = false,
    selectedType = null
}: MedianDurationTrendsProps) {
    const [showPercentiles, setShowPercentiles] = useState(true);

    const availableTypes = useMemo(() => {
        if (!data?.medianByDay?.length || !includeByType) return [];

        const types = Array.from(new Set(
            data.medianByDay
                .filter(d => d.type)
                .map(d => d.type)
                .filter((type): type is string => !!type)
        ));

        return types;
    }, [data, includeByType]);

    const { chartData, hasData, yAxisMax, yAxisMin } = useMemo(() => {
        if (!data?.medianByDay?.length) {
            return { chartData: null, hasData: false, yAxisMax: 180, yAxisMin: 0 };
        }

        const uniqueDays = Array.from(new Set(
            data.medianByDay.map(d => d.day)
        )).sort();

        const validData = data.medianByDay.filter(d => {
            const isValidValue = d.median >= 0 && d.median < 1000 &&
                (!d.p25 || d.p25 >= 0) && (!d.p75 || d.p75 >= 0) &&
                (!d.p25 || !d.p75 || d.p75 >= d.p25);
            return isValidValue && (selectedType ? d.type === selectedType : true);
        });

        if (validData.length === 0) {
            return { chartData: null, hasData: false, yAxisMax: 180, yAxisMin: 0 };
        }

        const sortedMedians = [...validData].map(d => d.median).sort((a, b) => a - b);
        const p99Index = Math.floor(sortedMedians.length * 0.99);
        const maxValue = sortedMedians[p99Index] || 180;

        const yAxisMaxValue = Math.min(Math.ceil(maxValue * 1.2 / 10) * 10, 1000);
        const yAxisMinValue = 0;

        let datasets: any[] = [];

        if (includeByType && availableTypes.length > 0 && !selectedType) {
            datasets = availableTypes.map(type => {
                const typeData = validData.filter(d => d.type === type);

                const medianValues = uniqueDays.map(day => {
                    const dayData = typeData.find(d => d.day === day);
                    return dayData ? dayData.median : null;
                });

                const dataset: any = {
                    label: getTypeDisplayName(type, t),
                    data: medianValues,
                    fill: false,
                    borderColor: getColorForType(type),
                    tension: 0.2
                };

                if (showPercentiles) {
                    dataset.p25 = uniqueDays.map(day => {
                        const dayData = typeData.find(d => d.day === day);
                        return dayData ? dayData.p25 : null;
                    });

                    dataset.p75 = uniqueDays.map(day => {
                        const dayData = typeData.find(d => d.day === day);
                        return dayData ? dayData.p75 : null;
                    });
                }

                return dataset;
            });
        } else {
            const medianValues = uniqueDays.map(day => {
                const dayData = selectedType
                    ? validData.find(d => d.day === day && d.type === selectedType)
                    : validData.find(d => d.day === day);
                return dayData ? dayData.median : null;
            });

            const color = selectedType ? getColorForType(selectedType) : 'rgba(54, 162, 235, 1)';
            const label = selectedType
                ? `${getTypeDisplayName(selectedType, t)} - ${t`Median`}`
                : t`Median`;

            const dataset: any = {
                label: label,
                data: medianValues,
                fill: false,
                borderColor: color,
                tension: 0.2
            };

            if (showPercentiles) {
                dataset.p25 = uniqueDays.map(day => {
                    const dayData = selectedType
                        ? validData.find(d => d.day === day && d.type === selectedType)
                        : validData.find(d => d.day === day);
                    return dayData ? dayData.p25 : null;
                });

                dataset.p75 = uniqueDays.map(day => {
                    const dayData = selectedType
                        ? validData.find(d => d.day === day && d.type === selectedType)
                        : validData.find(d => d.day === day);
                    return dayData ? dayData.p75 : null;
                });
            }

            datasets = [dataset];
        }

        const finalChartData: ChartData<'line'> = {
            labels: uniqueDays,
            datasets
        };

        return {
            chartData: finalChartData,
            hasData: true,
            yAxisMax: yAxisMaxValue,
            yAxisMin: yAxisMinValue
        };
    }, [data, includeByType, availableTypes, selectedType, showPercentiles]);

    const options = useMemo(() => {
        return {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    position: 'top' as const,
                    labels: {
                        boxWidth: 15,
                        padding: 10
                    }
                },
                tooltip: {
                    callbacks: {
                        label: (context: TooltipItem<'line'>) => {
                            if (!chartData) return '';

                            const dataset = chartData.datasets[context.datasetIndex];
                            let label = dataset.label || '';
                            if (label) label += ': ';

                            label += `${context.formattedValue} ${t`minutes`}`;

                            if (showPercentiles) {
                                const p25 = (dataset as any).p25?.[context.dataIndex];
                                const p75 = (dataset as any).p75?.[context.dataIndex];
                                if (p25 !== null && p25 !== undefined && p75 !== null && p75 !== undefined) {
                                    label += ` (${t`range`}: ${p25.toFixed(1)}-${p75.toFixed(1)})`;
                                }
                            }

                            return label;
                        }
                    }
                },
                percentileRanges: {
                    id: 'percentileRanges',
                    beforeDraw: (chart: Chart) => {
                        if (!showPercentiles || !chartData) return;

                        const { ctx } = chart;
                        chartData.datasets.forEach((dataset, datasetIndex) => {
                            const anyDataset = dataset as any;
                            if (!anyDataset.p25 || !anyDataset.p75) return;

                            const meta = chart.getDatasetMeta(datasetIndex);

                            ctx.save();
                            ctx.fillStyle = hexToRgba(getColorForType(dataset.label || ''), 0.2);

                            for (let i = 0; i < meta.data.length; i++) {
                                if (anyDataset.p25[i] === null || anyDataset.p75[i] === null) continue;

                                const p25Y = chart.scales['y'].getPixelForValue(anyDataset.p25[i]);
                                const p75Y = chart.scales['y'].getPixelForValue(anyDataset.p75[i]);
                                const x = meta.data[i].x;

                                let width = 20;
                                if (i < meta.data.length - 1) {
                                    width = meta.data[i + 1].x - x;
                                } else if (i > 0) {
                                    width = x - meta.data[i - 1].x;
                                }

                                ctx.fillRect(x - width / 2, p25Y, width, p75Y - p25Y);
                            }

                            ctx.restore();
                        });
                    }
                }
            },
            scales: {
                x: {
                    title: {
                        display: true,
                        text: t`Date`
                    }
                },
                y: {
                    beginAtZero: true,
                    suggestedMin: yAxisMin,
                    suggestedMax: yAxisMax,
                    title: {
                        display: true,
                        text: t`minutes`
                    }
                }
            }
        };
    }, [chartData, showPercentiles, yAxisMin, yAxisMax]);

    return (
        <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h2">{title}</Typography>
                {data?.dataQuality && (
                    <Chip
                        label={`${t`Consultations analyzed`}: ${data.dataQuality.validDurations}`}
                        color="primary"
                    />
                )}
            </Stack>

            <FormControlLabel
                control={
                    <Switch
                        checked={showPercentiles}
                        onChange={(e) => setShowPercentiles(e.target.checked)}
                    />
                }
                label={t`Show percentiles`}
                sx={{ mb: 2 }}
            />

            {hasData && chartData ? (
                <Box sx={{ height: 400 }}>
                    <Line data={chartData} options={options} />
                </Box>
            ) : (
                <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="300px">
                    <NoResults style={{ maxWidth: '40%', height: 'auto' }} />
                    <Typography variant="subtitle1" color="text.secondary" mt={2}>
                        {t`No valid data`}
                    </Typography>
                </Box>
            )}
        </Box>
    );
}

export default MedianDurationTrendsComponent;
